import { Grid, Box, Typography, Paper, TextField } from "@mui/material";
import BreadCrumb from "../../../components/BreadCrumb";
import Layout from "../../../components/Layout";
import Button from "../../../components/Button";
import useStyles from "../styles";
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import Modal from "../../../components/Modal";
import InputGroup from "../../../components/FormComponent/InputGroup";
import TextAreaGroup from "../../../components/FormComponent/TextAreaGroup";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { httprequest } from "../../../data/api";
import { useParams } from "react-router-dom";
import PageLoader from "../../../components/PageLoader";
import Badge from "../../../components/Badge";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useUserState } from "../../../context/UserContext";
import logo from "../../../assets/images/logo.svg";

const TRANSACTIONS = "/api/v1/invoices/student-invoices/transactions";
const MAKE_PAYMENT = "/api/v1/invoices/student-invoices";
const UPLOAD_ENDPOINT = "/api/v1/upload";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (!error.message?.includes("toggle") && !error.stack?.includes("typedCharts")) {
      // eslint-disable-next-line no-console
      console.log("Error caught by boundary:", error);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.error?.message?.includes("toggle") ||
            this.state.error?.stack?.includes("typedCharts")) {
        return this.props.children;
      }

      return this.props.fallback;
    }

    return this.props.children;
  }
}
const TransactionDetails = () => {
  const classes = useStyles();
  const { id } = useParams();
  const { appLogo } = useUserState();
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingOfflinePayment, setIsProcessingOfflinePayment] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [comment, setComment] = useState("");

  const [receiptData, setReceiptData] = useState({
    title: "",
    file: null
  });

  const paymentMethods = [
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cash", label: "Cash" }
  ];

  const getTransactionById = useCallback(() => {
    if (!id) {
      toast.error("Transaction ID is missing");

      return;
    }

    setIsLoading(true);
    httprequest
      .get(`${TRANSACTIONS}/${id}`)
      .then((res) => {
        setTransaction(res?.data?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message || "Failed to fetch transaction details");
      });
  }, [id]);

  useEffect(() => {
    getTransactionById();
  }, [getTransactionById]);

  const handleReuploadReceipt = () => {
    setShowPaymentMethodModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a JPG, PNG, or PDF file");

      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");

      return;
    }

    setReceiptData(prev => ({
      ...prev,
      file
    }));
  };

  const handleRemoveFile = () => {
    setReceiptData(prev => ({
      ...prev,
      file: null
    }));
  };

  const processOfflinePayment = async () => {
    if (!transaction?.invoice_no) {
      toast.error("Invoice information is missing");

      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Please enter a valid payment amount");

      return;
    }

    if (selectedPaymentMethod?.value === "bank_transfer" && !receiptData.file) {
      toast.error("Please upload a receipt for bank transfer");

      return;
    }

    setIsProcessingOfflinePayment(true);

    try {
      const formData = new FormData();
      formData.append("file", receiptData.file);

      const uploadResponse = await httprequest.post(UPLOAD_ENDPOINT, formData);

      if (uploadResponse.status === 200 && uploadResponse.data.data) {
        const upload_id = uploadResponse.data.data[0]?.upload_id;
        const file_url = uploadResponse.data.data[0]?.file_url;

        const paymentPayload = {
          amount: parseFloat(paymentAmount),
          reference: `invoice-${transaction.invoice_no}-${Date.now()}`,
          payment_method: selectedPaymentMethod?.label || "Bank Transfer",
          description: comment || `Payment for invoice ${transaction.invoice_no}`,
          receipts: [
            {
              file_url,
              upload_id
            }
          ]
        };

        const paymentResponse = await httprequest.post(
          `${MAKE_PAYMENT}/${transaction.invoice_no}/transactions`,
          paymentPayload
        );

        toast.success(paymentResponse?.data?.message || "Payment submitted successfully!");
        setShowPaymentMethodModal(false);
        // Refresh transaction data
        getTransactionById();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Payment submission failed");
    } finally {
      setIsProcessingOfflinePayment(false);
    }
  };

  const handlePaymentMethodSelect = () => {
    if (!selectedPaymentMethod) {
      toast.error("Please select a payment method");

      return;
    }

    if (selectedPaymentMethod.value === "bank_transfer" || selectedPaymentMethod.value === "cash") {
      processOfflinePayment();
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const rawValue = value.replace(/,/g, "");

    if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
      setPaymentAmount(rawValue);
    }
  };

  const formatAmountWithCommas = (value) => {
    if (!value) return "";
    const numberValue = value.toString().replace(/,/g, "");

    return numberValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--//--";
    try {
      return new Date(dateString).toLocaleDateString("en-GB");
    } catch {
      return dateString;
    }
  };

  const downloadTransactionReceipt = async () => {
    if (!transaction) {
      toast.error("No transaction data available");

      return;
    }

    try {
      const receiptContainer = document.createElement("div");
      receiptContainer.style.padding = "20px";
      receiptContainer.style.backgroundColor = "white";
      receiptContainer.style.position = "absolute";
      receiptContainer.style.left = "-9999px";
      receiptContainer.style.top = "0";

      const originalContainer = document.querySelector(".transaction-details-container");
      const clonedContent = originalContainer.cloneNode(true);

      const logoSection = document.createElement("div");
      logoSection.style.textAlign = "center";
      logoSection.style.marginBottom = "20px";
      logoSection.style.borderBottom = "2px solid #f0f0f0";
      logoSection.style.paddingBottom = "20px";

      const logoImg = document.createElement("img");
      logoImg.src = appLogo || logo;
      logoImg.alt = "School Logo";
      logoImg.style.maxWidth = "150px";
      logoImg.style.maxHeight = "80px";
      logoImg.style.marginBottom = "10px";

      const receiptTitle = document.createElement("h1");
      receiptTitle.textContent = transaction?.invoice_name;
      receiptTitle.style.margin = "0 0 15px 0";
      receiptTitle.style.color = "#1976d2";
      receiptTitle.style.fontSize = "24px";
      receiptTitle.style.fontWeight = "bold";

      logoSection.appendChild(logoImg);
      logoSection.appendChild(receiptTitle);

      receiptContainer.appendChild(logoSection);
      receiptContainer.appendChild(clonedContent);

      document.body.appendChild(receiptContainer);

      const canvas = await html2canvas(receiptContainer, {
        scale: 1.5,
        useCORS: true,
        logging: false
      });

      document.body.removeChild(receiptContainer);

      const imgData = canvas.toDataURL("image/png");
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);

      const fileName = `receipt-${transaction.reference || transaction.id}.pdf`;
      pdf.save(fileName);
      toast.success("Receipt downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate receipt");
    }
  };

  return (
    <ErrorBoundary>
      <Layout children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/guardian/transaction"
                link_one_name="Transaction History"
                active="Transaction Details"
                description="Details of your transaction details"
              />
            </div>
          </div>

          {isLoading ? (
            <PageLoader />
          ) : (
            <>
              <Box sx={{ p: 4, background: "white" }}>
                <Box sx={{ mb: 3, textAlign: "left" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transaction Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                See your transaction details here
                  </Typography>
                </Box>
                <div className="transaction-details-container">
                  <Paper elevation={0}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Total Amount
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: 16, textAlign: "left" }}>
                          {transaction?.amount}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Amount Paid
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: 16, textAlign: "left" }}>
                          {transaction?.invoice?.amount_paid}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}>
                          <Box>
                            <Typography sx={{ color: "gray", fontSize: 13 }}>
                        Payment Type
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                              {transaction?.payment_method}
                            </Typography>
                          </Box>
                          <Badge status={transaction?.status} />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Method
                        </Typography>
                        <Typography sx={{ fontWeight: 500, textAlign: "left" }}>
                          {transaction?.payment_method}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Paid For
                        </Typography>
                        <Typography sx={{ fontWeight: 500, textAlign: "left" }}>
                          {transaction?.student?.last_name} {transaction?.student?.first_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Paid To
                        </Typography>
                        <Typography sx={{ fontWeight: 500, textAlign: "left" }}>
                          {transaction?.paidTo || "--//--"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Reference Number
                        </Typography>
                        <Typography sx={{ fontWeight: 500, textAlign: "left" }}>
                          {transaction?.reference || "--//--"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography sx={{ color: "gray", fontSize: 13, textAlign: "left" }}>
                    Date Paid
                        </Typography>
                        <Typography sx={{ fontWeight: 500, textAlign: "left" }}>
                          {formatDate(transaction?.created_at)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ color: "gray", fontSize: 13, mb: 1, textAlign: "left" }}>
                    Description
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#f9fafb",
                            p: 2,
                            paddingBottom: "1.5rem",
                            borderRadius: "8px",
                            fontSize: "14px",
                            color: "#333",
                            textAlign: "left"
                          }}
                        >
                          {transaction?.description || "No description available"}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </div>

                {transaction?.status === "approved" &&
                                          <Box sx={{ textAlign: "center", display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
                                            <Button
                                              variant="primaryoutline"
                                              buttonSize="fluid"
                                              color="btnfontprimary"
                                              buttonName="Download Receipts"
                                              onClick={downloadTransactionReceipt}
                                              disabled={!transaction}
                                            />
                                          </Box>
                }
              </Box>

              {transaction?.status === "declined" && (
                <Box sx={{ p: 4, background: "white", textAlign: "left", marginTop: "1.5rem" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#D32F2F" }}>
                Payment Declined
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Your payment was declined
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ color: "gray", fontSize: 13 }}>
                    Method
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }}>
                        {transaction?.payment_method}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ color: "gray", fontSize: 13 }}>
                    Reference
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }}>
                        {transaction?.reference || "--//--"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ color: "gray", fontSize: 13 }}>
                    Date
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }}>
                        {formatDate(transaction?.created_at)}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography sx={{ color: "gray", fontSize: 13, mb: 1 }}>
                Comment
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    disabled
                    value={transaction?.audits?.[0]?.comment}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f9fafb",
                        color: "#333"
                      },
                      "& .MuiOutlinedInput-notchedOutline.Mui-disabled": {
                        borderColor: "#eee"
                      }
                    }}
                  />

                  <Box sx={{ textAlign: "center", display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
                    <Button
                      variant="primary"
                      color="btndefault"
                      buttonSize="fluid"
                      buttonName="Re-upload Receipts"
                      onClick={handleReuploadReceipt}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Payment Method Modal */}
          <Modal
            title="Re-Upload Receipt"
            subtitle="Select payment method and provide payment details"
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12}>
                  <InputGroup
                    label="Invoice No"
                    inputType="text"
                    inputName="invoice_no"
                    placeHolder="Invoice No"
                    value={transaction?.invoice_no}
                    disabled={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputGroup
                    label="Amount Paid"
                    inputType="text"
                    inputName="amount"
                    placeHolder="Enter amount"
                    value={formatAmountWithCommas(paymentAmount)}
                    onChange={handleAmountChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <div className="multiple-select">
                    <p>Select Payment Method</p>
                    <Select
                      label="Payment Method"
                      options={paymentMethods}
                      value={selectedPaymentMethod}
                      onChange={(selectedOption) => setSelectedPaymentMethod(selectedOption)}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      menuShouldScrollIntoView={false}
                      isSearchable={false}
                    />
                  </div>
                </Grid>

                {(selectedPaymentMethod?.value === "bank_transfer" || selectedPaymentMethod?.value === "cash") && (
                  <Grid item xs={12}>
                    <p className="text-align-left no-margin-block">
                      <strong>Upload Receipt</strong>
                      <span className="input-required">*</span>
                    </p>
                    <div className={classes.pickcontainer}>
                      <input
                        type="file"
                        required
                        className={classes.picker}
                        onChange={handleFileChange}
                        name="file"
                        accept=".png, .jpg, .jpeg, .pdf"
                      />

                      <div className={classes.uploadflex}>
                        <div>
                          <Icon
                            icon="icon-park-outline:upload-two"
                            className={classes.uploadicon}
                          />
                        </div>
                        {receiptData.file?.name ? (
                          <div>
                            <h2 className={classes.uploadtitle}>
                              {receiptData.file?.name}
                            </h2>
                            <p className={classes.uploadsubtitle}>
                              {(receiptData.file.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className={classes.uploadtitle}>
                          Tap to Upload
                            </p>
                            <p className={classes.uploadsubtitle}>
                          JPG, PNG, PDF | 10MB max.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className={classes.rightpicker}>
                        {receiptData.file?.name && (
                          <div
                            className={classes.uploadbutton}
                            onClick={handleRemoveFile}
                          >
                            <Icon icon="zondicons:minus-solid" /> Remove File
                          </div>
                        )}
                      </div>
                    </div>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextAreaGroup
                    label="Comment (Optional)"
                    inputName="comment"
                    margin="normal"
                    placeholder="Add any additional comments about this payment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </Grid>
              </Grid>
            }
            modalFooter={
              <div className="flex gap-10">
                <Button
                  variant="primary"
                  buttonSize="full"
                  color="btndefault"
                  buttonName={isProcessingOfflinePayment ? "Processing..." : "Submit Payment"}
                  onClick={handlePaymentMethodSelect}
                  type="button"
                />
              </div>
            }
            isopen={showPaymentMethodModal}
            closeModal={() => {
              setShowPaymentMethodModal(false);
              setPaymentAmount("");
              setComment("");
              setReceiptData({ title: "", file: null });
              setSelectedPaymentMethod(null);
            }}
          />

          {/* <ToastContainer /> */}
        </>
      }
      />
    </ErrorBoundary>
  );
};

export default TransactionDetails;
